import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Music, LogOut, ListMusic, FileText, Upload, Save, Loader2, Video, Image as ImageIcon, User, Power, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [videoUploading, setVideoUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [posterUploading, setPosterUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings(data || {});
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSetting = async (key, value) => {
    try {
      const { data } = await axios.put(`${API}/settings`, { [key]: value });
      setSettings(data);
      toast.success('Saved');
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const uploadFile = async (file, endpoint) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${API}${endpoint}`, fd);
    return data.url;
  };

  const handleVideoUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setVideoUploading(true);
    try {
      const relUrl = await uploadFile(f, '/upload/video');
      const absUrl = `${BACKEND_URL}${relUrl}`;
      await saveSetting('hero_video_url', absUrl);
    } catch (err) {
      toast.error('Video upload failed');
    } finally {
      setVideoUploading(false);
    }
  };

  const handlePosterUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setPosterUploading(true);
    try {
      const relUrl = await uploadFile(f, '/upload/cover');
      const absUrl = `${BACKEND_URL}${relUrl}`;
      await saveSetting('hero_poster_url', absUrl);
    } catch (err) {
      toast.error('Poster upload failed');
    } finally {
      setPosterUploading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setPhotoUploading(true);
    try {
      const relUrl = await uploadFile(f, '/upload/cover');
      const absUrl = `${BACKEND_URL}${relUrl}`;
      await saveSetting('artist_photo_url', absUrl);
    } catch (err) {
      toast.error('Photo upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };

  const clearField = (key) => saveSetting(key, '');

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4 text-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Music className="text-[#00F0FF]" size={28} />
            <div>
              <h1 className="text-3xl font-heading font-medium tracking-tight" data-testid="settings-title">Site Settings</h1>
              <p className="text-[#A1A1AA] text-sm">Hero video, poster image, and artist photo</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {admin?.email && <span className="text-sm text-[#A1A1AA]">{admin.email}</span>}
            <Button variant="outline" onClick={() => navigate('/admin/releases')} className="border-[#27272A] hover:bg-[#18181B] rounded-full" data-testid="nav-releases-button">
              <ListMusic size={16} className="mr-2" /> Releases
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/pages')} className="border-[#27272A] hover:bg-[#18181B] rounded-full" data-testid="nav-pages-button">
              <FileText size={16} className="mr-2" /> Site Pages
            </Button>
            <Button variant="outline" onClick={async () => { await logout(); navigate('/'); }} className="border-[#27272A] hover:bg-[#18181B] rounded-full" data-testid="admin-logout-button">
              <LogOut size={16} className="mr-2" /> Log out
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="text-[#00F0FF] animate-spin" size={40} />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Site Live / Maintenance */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 md:p-8" data-testid="maintenance-card">
              <div className="flex items-center gap-2 mb-2">
                <Power className={settings.maintenance_mode ? 'text-yellow-400' : 'text-[#00F0FF]'} size={20} />
                <h2 className="text-xl font-heading font-semibold">
                  Site is {settings.maintenance_mode ? 'in Maintenance' : 'Live'}
                </h2>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-5">
                Flip this switch to instantly show buyers a friendly "Under maintenance" page while you upload new releases or make changes. Admin routes stay accessible either way.
              </p>
              <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                <span
                  role="switch"
                  aria-checked={!settings.maintenance_mode}
                  className={`relative inline-flex h-7 w-14 rounded-full transition-colors ${
                    settings.maintenance_mode ? 'bg-yellow-500/30' : 'bg-[#00F0FF]'
                  }`}
                  data-testid="maintenance-toggle"
                  onClick={() => saveSetting('maintenance_mode', !settings.maintenance_mode)}
                >
                  <span
                    className={`inline-block h-6 w-6 rounded-full bg-white transform transition-transform ${
                      settings.maintenance_mode ? 'translate-x-0.5 translate-y-0.5' : 'translate-x-[30px] translate-y-0.5'
                    }`}
                  />
                </span>
                <span
                  className="text-sm font-medium"
                  onClick={() => saveSetting('maintenance_mode', !settings.maintenance_mode)}
                >
                  {settings.maintenance_mode ? 'Show live site' : 'Enable maintenance mode'}
                </span>
              </label>

              <div className="mt-5">
                <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2">
                  Maintenance message (optional)
                </label>
                <textarea
                  rows={2}
                  value={settings.maintenance_message || ''}
                  onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                  onBlur={(e) => saveSetting('maintenance_message', e.target.value)}
                  placeholder="Back in 15 mins — uploading new packs!"
                  className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm text-white focus:border-[#00F0FF] focus:outline-none"
                  data-testid="maintenance-message-input"
                />
              </div>
            </div>

            {/* Social Links (public — shown on Maintenance page + share targets) */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 md:p-8" data-testid="social-links-card">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="text-[#00F0FF]" size={20} />
                <h2 className="text-xl font-heading font-semibold">Social Links</h2>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-5">
                Public URLs shown on the maintenance page and used as your fangate follow targets. Leave a field empty to hide that platform. Paste the FULL URL (starts with <span className="text-[#D4D4D8] font-mono">https://</span>).
              </p>
              {[
                { key: 'spotify_url',    label: 'Spotify',    icon: 'fa-brands fa-spotify',    color: '#1DB954', placeholder: 'https://open.spotify.com/artist/…' },
                { key: 'soundcloud_url', label: 'SoundCloud', icon: 'fa-brands fa-soundcloud', color: '#FF5500', placeholder: 'https://soundcloud.com/…' },
                { key: 'instagram_url',  label: 'Instagram',  icon: 'fa-brands fa-instagram',  color: '#E1306C', placeholder: 'https://www.instagram.com/…' },
                { key: 'tiktok_url',     label: 'TikTok',     icon: 'fa-brands fa-tiktok',     color: '#25F4EE', placeholder: 'https://www.tiktok.com/@…' },
                { key: 'facebook_url',   label: 'Facebook',   icon: 'fa-brands fa-facebook',   color: '#1877F2', placeholder: 'https://www.facebook.com/…' },
                { key: 'messenger_url',  label: 'Messenger',  icon: 'fa-brands fa-facebook-messenger', color: '#00B2FF', placeholder: 'https://m.me/… or https://www.messenger.com/t/…' },
              ].map((s) => (
                <div key={s.key} className="mb-3">
                  <label className="block text-xs uppercase tracking-widest text-[#71717A] mb-2 inline-flex items-center gap-2">
                    <i className={s.icon} style={{ color: s.color }} /> {s.label}
                  </label>
                  <input
                    type="url"
                    value={settings[s.key] || ''}
                    onChange={(e) => setSettings({ ...settings, [s.key]: e.target.value })}
                    onBlur={(e) => saveSetting(s.key, e.target.value.trim())}
                    placeholder={s.placeholder}
                    className="w-full px-3 py-2 bg-[#0F0F11] border border-[#27272A] rounded-md text-sm text-white focus:border-[#00F0FF] focus:outline-none"
                    data-testid={`social-${s.key}`}
                  />
                </div>
              ))}
            </div>

            {/* Logo (used as browser favicon) */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 md:p-8" data-testid="logo-card">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon className="text-[#00F0FF]" size={20} />
                <h2 className="text-xl font-heading font-semibold">Logo & Favicon</h2>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-4">
                A square PNG works best. This shows up in the browser tab across the site.
              </p>
              {settings.logo_url ? (
                <div className="mb-4 flex items-center gap-3 p-3 bg-[#18181B] rounded-md border border-[#27272A]">
                  <img
                    src={settings.logo_url.startsWith('http') ? settings.logo_url : `${BACKEND_URL}${settings.logo_url}`}
                    alt="Site logo"
                    className="w-12 h-12 rounded bg-black object-contain"
                    data-testid="logo-preview"
                  />
                  <div className="flex-1 text-xs text-[#A1A1AA] font-mono truncate">{settings.logo_url}</div>
                  <button
                    onClick={() => clearField('logo_url')}
                    className="text-xs text-[#71717A] hover:text-red-400"
                    data-testid="clear-logo"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <p className="mb-3 text-xs text-[#71717A]">No logo set — using default favicon.</p>
              )}
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#18181B] border border-[#27272A] rounded-full cursor-pointer hover:bg-[#27272A] transition-colors">
                <Upload size={14} />
                <span className="text-sm">Upload logo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    try {
                      const url = await uploadFile(f, '/upload/cover');
                      await saveSetting('logo_url', `${BACKEND_URL}${url}`);
                    } catch { toast.error('Logo upload failed'); }
                  }}
                  className="hidden"
                  data-testid="upload-logo"
                />
              </label>
            </div>
            {/* Hero Video */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 md:p-8" data-testid="hero-video-card">
              <div className="flex items-center gap-2 mb-4">
                <Video className="text-[#00F0FF]" size={20} />
                <h2 className="text-xl font-heading font-semibold">Hero Background Video</h2>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-4">
                Upload an MP4 that will loop on the homepage. Parallax will apply automatically as visitors move their cursor.
              </p>
              {settings.hero_video_url ? (
                <div className="mb-4 flex items-center gap-3 p-3 bg-[#18181B] rounded-md border border-[#27272A]">
                  <video src={settings.hero_video_url} muted className="w-32 h-20 rounded object-cover bg-black" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">Current video</p>
                    <a href={settings.hero_video_url} target="_blank" rel="noreferrer" className="text-xs text-[#00F0FF] hover:underline break-all">{settings.hero_video_url}</a>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => clearField('hero_video_url')} className="border-[#27272A] hover:bg-[#18181B] rounded-full" data-testid="clear-hero-video">Remove</Button>
                </div>
              ) : (
                <p className="text-xs text-[#A1A1AA] mb-4 italic">No video uploaded — currently showing placeholder stock footage.</p>
              )}
              <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-[#27272A] rounded-lg cursor-pointer hover:border-[#00F0FF] transition-colors">
                {videoUploading ? (
                  <Loader2 className="text-[#00F0FF] animate-spin" size={24} />
                ) : (
                  <>
                    <Upload className="mb-2 text-[#A1A1AA]" size={22} />
                    <span className="text-sm text-[#A1A1AA]">Click to upload MP4 video</span>
                  </>
                )}
                <input type="file" accept="video/mp4,video/*" onChange={handleVideoUpload} className="hidden" disabled={videoUploading} data-testid="upload-hero-video" />
              </label>
            </div>

            {/* Hero Poster */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 md:p-8" data-testid="hero-poster-card">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon className="text-[#00F0FF]" size={20} />
                <h2 className="text-xl font-heading font-semibold">Hero Poster Image (optional)</h2>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-4">
                Shown while the video is loading or on devices that block autoplay.
              </p>
              {settings.hero_poster_url && (
                <div className="mb-4 flex items-center gap-3 p-3 bg-[#18181B] rounded-md border border-[#27272A]">
                  <img src={settings.hero_poster_url} alt="Poster" className="w-32 h-20 rounded object-cover bg-black" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">Current poster</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => clearField('hero_poster_url')} className="border-[#27272A] rounded-full">Remove</Button>
                </div>
              )}
              <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-[#27272A] rounded-lg cursor-pointer hover:border-[#00F0FF] transition-colors">
                {posterUploading ? (
                  <Loader2 className="text-[#00F0FF] animate-spin" size={24} />
                ) : (
                  <>
                    <Upload className="mb-2 text-[#A1A1AA]" size={22} />
                    <span className="text-sm text-[#A1A1AA]">Click to upload poster image</span>
                  </>
                )}
                <input type="file" accept="image/*" onChange={handlePosterUpload} className="hidden" disabled={posterUploading} data-testid="upload-hero-poster" />
              </label>
            </div>

            {/* Artist Photo */}
            <div className="bg-[#121214] border border-[#27272A] rounded-lg p-6 md:p-8" data-testid="artist-photo-card">
              <div className="flex items-center gap-2 mb-4">
                <User className="text-[#00F0FF]" size={20} />
                <h2 className="text-xl font-heading font-semibold">Artist Photo</h2>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-4">
                Shown at the top of your Artist Info page. Recommended: square photo, 800x800 or larger.
              </p>
              {settings.artist_photo_url && (
                <div className="mb-4 flex items-center gap-3 p-3 bg-[#18181B] rounded-md border border-[#27272A]">
                  <img src={settings.artist_photo_url} alt="Artist" className="w-24 h-24 rounded-full object-cover bg-black" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">Current photo</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => clearField('artist_photo_url')} className="border-[#27272A] rounded-full">Remove</Button>
                </div>
              )}
              <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-[#27272A] rounded-lg cursor-pointer hover:border-[#00F0FF] transition-colors">
                {photoUploading ? (
                  <Loader2 className="text-[#00F0FF] animate-spin" size={24} />
                ) : (
                  <>
                    <Upload className="mb-2 text-[#A1A1AA]" size={22} />
                    <span className="text-sm text-[#A1A1AA]">Click to upload artist photo</span>
                  </>
                )}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={photoUploading} data-testid="upload-artist-photo" />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettingsPage;
